import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { MENU_CATEGORIES } from '../data/mock';
import { Reveal, SplitText, Magnetic, AnimatedDivider } from '../components/effects';
import CTASection from '../components/CTASection';

const HeroBlock = () => (
  <section className="relative pt-[170px] lg:pt-[200px] pb-20 lg:pb-32 bg-[#F5F3EF] overflow-hidden">
    <div className="container-x relative">
      <div className="flex items-center gap-3 mb-7">
        <AnimatedDivider width="3rem" />
        <Reveal delay={100} y={8} duration={700}>
          <span className="text-[10.5px] tracking-[0.34em] uppercase text-[#9A7F4F] font-medium">
            Menü Yapısı
          </span>
        </Reveal>
      </div>

      <h1
        className="font-display text-[44px] sm:text-[64px] lg:text-[88px] leading-[0.98] tracking-[-0.022em] text-[#1A1A1A] max-w-5xl"
        style={{ fontWeight: 500 }}
      >
        <SplitText text="Mevsime uygun," delay={120} />
        <br />
        <span className="italic font-light text-[#9A7F4F]">
          <SplitText text="28 günlük döner menü." delay={520} />
        </span>
      </h1>

      <Reveal delay={1000}>
        <p className="mt-9 text-[16px] lg:text-[18px] text-[#1A1A1A]/70 max-w-2xl leading-[1.7]">
          Aşağıda genel kategorilerimizi ve örnek yemeklerimizi görebilirsiniz. Sektörünüze
          ve çalışan profilinize göre özelleştirilmiş menü, sözleşme aşamasında birlikte
          hazırlanır.
        </p>
      </Reveal>
    </div>
  </section>
);

const CategoryShowcase = () => {
  const [active, setActive] = useState(0);
  const cat = MENU_CATEGORIES[active];

  return (
    <section className="py-20 lg:py-28 bg-[#EFEBE3]">
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Tab list */}
          <div className="lg:col-span-4">
            <div>
              {MENU_CATEGORIES.map((c, i) => (
                <button
                  key={c.name}
                  onClick={() => setActive(i)}
                  data-testid={`menu-tab-${c.name}`}
                  className={`group w-full text-left px-1 py-6 border-b border-[#1A1A1A]/[0.10] flex items-center justify-between transition-colors ${
                    active === i ? '' : 'hover:bg-[#1A1A1A]/[0.02]'
                  }`}
                >
                  <div className="flex items-baseline gap-5">
                    <span
                      className={`text-[10.5px] numeric tracking-[0.32em] font-medium transition-colors ${
                        active === i ? 'text-[#9A7F4F]' : 'text-[#1A1A1A]/35'
                      }`}
                    >
                      0{i + 1}
                    </span>
                    <span
                      className={`font-display text-[24px] lg:text-[28px] tracking-[-0.005em] transition-colors ${
                        active === i ? 'text-[#1A1A1A] italic' : 'text-[#1A1A1A]/55 group-hover:text-[#1A1A1A]'
                      }`}
                      style={{ fontWeight: active === i ? 400 : 400 }}
                    >
                      {c.name}
                    </span>
                  </div>
                  <ChevronRight
                    size={16}
                    className={active === i ? 'text-[#9A7F4F]' : 'text-[#1A1A1A]/30'}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-8">
            <Reveal key={active} delay={100}>
              <div className="overflow-hidden rounded-sm shadow-[0_30px_80px_-30px_rgba(114,39,45,0.32)]">
                <div className="aspect-[16/8] overflow-hidden relative bg-[#72272D]">
                  <img
                    src={`${cat.image}?auto=format&fit=crop&q=85&w=1800`}
                    alt={cat.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#72272D]/95 via-[#72272D]/35 to-transparent" />
                  <div className="absolute bottom-7 left-7 lg:bottom-9 lg:left-9 text-[#F5F0E5]">
                    <div className="text-[10.5px] tracking-[0.32em] uppercase text-[#C9A875] mb-2.5 font-medium">
                      Kategori 0{active + 1}
                    </div>
                    <h2
                      className="font-display text-[40px] lg:text-[56px] leading-[1.04] tracking-[-0.012em]"
                      style={{ fontWeight: 500 }}
                    >
                      {cat.name}
                    </h2>
                  </div>
                </div>

                <div className="bg-[#F5F3EF] p-8 lg:p-12">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-2.5">
                    {cat.items.map((item, i) => (
                      <div
                        key={item}
                        className="flex items-baseline justify-between gap-4 py-3 border-b border-dashed border-[#1A1A1A]/15"
                      >
                        <span className="text-[14.5px] text-[#1A1A1A] tracking-wide">
                          {item}
                        </span>
                        <span className="text-[10.5px] tracking-[0.28em] text-[#9A7F4F]/65 numeric">
                          0{i + 1}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-9 flex flex-wrap items-center gap-7">
                    <Magnetic strength={0.18}>
                      <Link
                        to="/iletisim"
                        className="group inline-flex items-center gap-3 bg-[#1A1A1A] text-[#F5F3EF] px-7 py-[16px] text-[12px] tracking-[0.22em] uppercase font-medium rounded-sm transition-colors duration-300 hover:bg-[#9A7F4F]"
                        data-testid="full-menu-cta"
                      >
                        Tam Menü Talep Et
                        <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Magnetic>
                    <span className="text-[12px] text-[#1A1A1A]/55">
                      PDF olarak da paylaşılabilir
                    </span>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

const NotesBlock = () => {
  const items = [
    {
      title: 'Mevsim Yönetimi',
      body: 'İlkbahar–Yaz / Sonbahar–Kış olarak iki ana menü; ayda bir revizyon.',
    },
    {
      title: 'Diyetisyen Onayı',
      body: 'Her menü, kalori ve makro besin dengesi açısından bağımsız onaydan geçer.',
    },
    {
      title: 'Özel Beslenme',
      body: 'Diyabet, çölyak, vejetaryen, vegan, alerjen-free seçenekleri standart.',
    },
  ];

  return (
    <section className="py-20 lg:py-28 bg-[#F5F3EF]">
      <div className="container-x grid md:grid-cols-3 gap-x-10 gap-y-12">
        {items.map((item, i) => (
          <Reveal key={item.title} delay={i * 140} y={24}>
            <div className="border-t border-[#9A7F4F]/35 pt-6">
              <div className="text-[10.5px] numeric tracking-[0.32em] uppercase text-[#9A7F4F] font-medium mb-4">
                0{i + 1}
              </div>
              <h3
                className="font-display text-[24px] lg:text-[28px] text-[#1A1A1A] italic leading-[1.2] tracking-[-0.005em]"
                style={{ fontWeight: 400 }}
              >
                {item.title}
              </h3>
              <p className="mt-4 text-[14px] text-[#1A1A1A]/68 leading-[1.7]">{item.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const Menu = () => {
  return (
    <>
      <HeroBlock />
      <CategoryShowcase />
      <NotesBlock />
      <CTASection />
    </>
  );
};

export default Menu;
